/**
 * <OptionCard> — une option en carte, sélectionnable (molecule).
 *
 * La brique des parcours parent qui demandent « lequel ? » : quelle séance, quel tuteur,
 * combien de minutes de retard, quel motif. Elle ne possède pas la sélection — elle est
 * contrôlée (`selected` + `onPress`), comme `<Checkbox>`.
 *
 * Réutilise `<Card>` pour la surface (aucun fond dessiné à la main), `<Badge>` pour la chip
 * et `<FocusRing>` pour l'anneau de focus. `leading` est une fente : un `<Avatar>` pour un
 * tuteur en tête. `description` prend du texte, ou une ligne déjà composée — celle d'une séance
 * (`<SessionDetailsLine>`).
 *
 * Indisponible ≠ éteint : quand l'option est refusée, le libellé s'atténue mais
 * `disabledReason` reste lisible — c'est la seule information utile à ce moment-là.
 */
import React from "react"
import { Pressable, View } from "react-native"
import { StyleSheet, useUnistyles } from "react-native-unistyles"

import { type SemanticColorKey } from "../../theme/tokens/colors"
import { Badge, type BadgeVariant } from "../../atoms/Badge"
import { CHIP_HEIGHT } from "../../atoms/Badge/Badge"
import { badgeFace } from "../../atoms/Badge/badgeFace"
import { FocusRing, focusRingHost } from "../../atoms/FocusRing"
import { Icon } from "../../atoms/icons/Icon"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"
import { Card } from "../Card"

/** `<Pressable>` ne type que `pressed` ; react-native-web ajoute `focused` à l'exécution. */
type PressableVisualState = { pressed: boolean; focused?: boolean }

/**
 * Le contour de la carte. La sélection se lit au contour, dans la couleur de l'univers, et à la
 * coche — pas à un fond teinté, qui ferait disparaître une chip lavande posée dessus. Il fait 2 px
 * dans les deux états : la sélection ne doit pas décaler la carte d'un pixel à chaque tap. Exporté
 * pour que son squelette pose le même, non choisi.
 */
export const optionCardContour = StyleSheet.create((theme) => ({
    card: (selected: boolean, tagColor?: SemanticColorKey) => ({
        borderWidth: 2,
        borderColor: tagColor != null ? theme.colors[tagColor] : selected ? theme.colors.role : theme.colors.border,
    }),
}))

export type OptionCardProps = {
    label: string
    /**
     * Une ligne de contexte sous le libellé — la matière d'un tuteur. Un texte s'écrit à l'encre
     * secondaire ; un élément (la ligne d'une séance) se pose tel quel.
     */
    description?: React.ReactNode
    /**
     * Une seconde ligne, sous la description : une règle qui vaut pour CETTE option (« Possible
     * jusqu'à 15h19 »). Absente, la carte est celle d'avant.
     */
    note?: string
    selected: boolean
    disabled?: boolean
    /** Pourquoi l'option est refusée. Affiché seulement quand `disabled`. */
    disabledReason?: string
    /** Chip à droite du libellé — « Recommandé », « Complet ». */
    badge?: string
    /** Variante de la chip — défaut `neutral`. */
    badgeVariant?: BadgeVariant
    /** Fente de tête : un `<Avatar>`, un `<Icon>`, une pastille. */
    leading?: React.ReactNode
    /**
     * Comment la sélection se lit. `check` (défaut) : la coche à droite. `radio` : un bouton radio
     * en tête, pour un choix entre options qui se lisent comme des phrases (« Si Camille ne peut
     * pas ? »). `radio-trailing` : le bouton radio à droite, toujours visible — un choix unique
     * entre cartes qui portent déjà un visage en tête (« Une place chez Camille »).
     */
    control?: "check" | "radio" | "radio-trailing"
    /**
     * Une étiquette posée sur le bord haut de la carte (« Recommandé ») : la carte prend alors le
     * contour de sa couleur, choisie ou non — c'est la radio qui dit la sélection.
     */
    tag?: string
    /** Variante de l'étiquette — défaut `success`. */
    tagVariant?: BadgeVariant
    onPress: () => void
    testID?: string
}

export function OptionCard(props: OptionCardProps) {
    const {
        label,
        description,
        note,
        selected,
        disabled = false,
        disabledReason,
        badge,
        badgeVariant = "neutral",
        leading,
        control = "check",
        tag,
        tagVariant = "success",
        onPress,
        testID,
    } = props

    const { theme } = useUnistyles()
    const labelColor: SemanticColorKey = disabled ? "text-muted" : "text-strong"
    const tagColor = tag != null ? badgeFace(tagVariant, false).bg : undefined

    return (
        <Pressable
            testID={testID}
            disabled={disabled}
            onPress={onPress}
            accessibilityRole="radio"
            accessibilityLabel={label}
            accessibilityState={{ selected, disabled }}
            style={({ pressed }: PressableVisualState) => [
                focusRingHost,
                pressed && !disabled ? styles.pressed : undefined,
                tag != null ? styles.tagged : undefined,
            ]}
        >
            {({ focused }: PressableVisualState) => (
                <>
                    <FocusRing visible={focused === true && !disabled} radius={theme.radius.lg} />
                    <Card
                        p="3"
                        style={[
                            optionCardContour.card(selected, tagColor),
                            tag != null ? styles.taggedCard : undefined,
                        ]}
                    >
                        <Row gap="3" align="center">
                            {control === "radio" && <View style={radio.dot(selected)} />}
                            {leading}
                            <Stack gap="1" style={styles.body}>
                                <Row gap="2" align="center" style={styles.labelRow}>
                                    <Text size="text-base" weight="bold" color={labelColor} style={styles.flex}>
                                        {label}
                                    </Text>
                                    {badge != null && <Badge text={badge} variant={badgeVariant} tinted />}
                                </Row>
                                {typeof description === "string" ? (
                                    <Text size="text-sm" color="text-muted">
                                        {description}
                                    </Text>
                                ) : (
                                    description
                                )}
                                {note != null && note !== "" && (
                                    <Text size="text-sm" weight="semibold" color="text-muted">
                                        {note}
                                    </Text>
                                )}
                                {disabled && disabledReason != null && (
                                    <Text size="text-sm" weight="semibold" color="warning-strong">
                                        {disabledReason}
                                    </Text>
                                )}
                            </Stack>
                            {control === "check" && selected && <Icon name="check-circle" size={20} color="role" />}
                            {control === "radio-trailing" && <View style={radio.dot(selected)} />}
                        </Row>
                    </Card>
                    {tag != null && (
                        <View style={styles.tag} pointerEvents="none">
                            <Badge text={tag} variant={tagVariant} tinted={false} />
                        </View>
                    )}
                </>
            )}
        </Pressable>
    )
}

const RADIO_SIZE = 22

/** Le bouton radio : un anneau fin, qui s'épaissit dans la couleur de l'univers une fois choisi. */
const radio = StyleSheet.create((theme) => ({
    dot: (selected: boolean) => ({
        width: RADIO_SIZE,
        height: RADIO_SIZE,
        borderRadius: RADIO_SIZE / 2,
        borderWidth: selected ? 7 : 2,
        borderColor: selected ? theme.colors.role : theme.colors.border,
    }),
}))

const styles = StyleSheet.create((theme) => ({
    pressed: { transform: [{ scale: 0.98 }] },
    /** La moitié de l'étiquette dépasse au-dessus de la carte : on lui garde sa place. */
    tagged: { paddingTop: CHIP_HEIGHT / 2 },
    taggedCard: { paddingTop: theme.spacing["4"] },
    tag: { position: "absolute" as const, top: 0, left: theme.spacing["3"] },
    body: { flexShrink: 1, flexGrow: 1 },
    /** Étroit, la chip passe à la ligne : sinon c'est le libellé qui se réduit à un mot par ligne. */
    labelRow: { flexWrap: "wrap" },
    flex: { flexShrink: 1 },
}))
